import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { api, formatApiError } from "../lib/api";

// Iter-158.13.2.20f.42 — compact Add / Edit / Delete section for
// Department Breakdown entries. Rendered inside both the Scene and
// Character workspace hosts. `scope` (`{scene_id}` or
// `{character_id}`) drives the list filter AND the default
// attachment on newly-added rows so the entry appears where the
// user is working. Editing an AI-originated row from any workspace
// mutates the same underlying document — the same row is visible
// in every Scene/Character workspace it relates to.

export default function DeptBreakdownSection({
    apiBase, projectId, scope,   // { scene_id?, character_id? }
    canContribute = true,
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newBody, setNewBody] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editBody, setEditBody] = useState("");
    const [editTitle, setEditTitle] = useState("");

    const refresh = useCallback(async () => {
        if (!apiBase || !projectId) return;
        setLoading(true);
        try {
            const params = {};
            if (scope?.scene_id) params.scene_id = scope.scene_id;
            if (scope?.character_id) params.character_id = scope.character_id;
            const { data } = await api.get(
                `${apiBase}/${projectId}/hm-breakdown-entries`,
                { params },
            );
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [apiBase, projectId, scope?.scene_id, scope?.character_id]);

    useEffect(() => { refresh(); }, [refresh]);

    const createManual = async () => {
        const body = (newBody || "").trim();
        if (!body || creating) return;
        setCreating(true);
        try {
            const payload = {
                title: (newTitle || "").trim() || null,
                body,
                scene_ids: scope?.scene_id ? [scope.scene_id] : [],
                character_ids: scope?.character_id ? [scope.character_id] : [],
            };
            await api.post(
                `${apiBase}/${projectId}/hm-breakdown-entries`, payload,
            );
            setNewTitle("");
            setNewBody("");
            toast.success("Added to Breakdown");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreating(false);
        }
    };

    const beginEdit = (row) => {
        setEditingId(row.id);
        setEditTitle(row.title || "");
        setEditBody(row.body || "");
    };

    const saveEdit = async () => {
        if (!editingId) return;
        const body = (editBody || "").trim();
        if (!body) { toast.error("Body cannot be empty"); return; }
        try {
            await api.patch(
                `${apiBase}/${projectId}/hm-breakdown-entries/${editingId}`,
                { title: editTitle || null, body },
            );
            setEditingId(null);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(
                `${apiBase}/${projectId}/hm-breakdown-entries/${id}`,
            );
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <section
            data-testid="dept-breakdown-section"
            className="mt-4 rounded-xl border border-white/10 bg-card p-4"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-semibold">
                    AI Hair & Makeup Breakdown
                </div>
                {loading ? (
                    <div className="text-[10px] text-neutral-500">loading…</div>
                ) : null}
            </div>

            <ul className="space-y-2">
                {rows.length === 0 ? (
                    <li className="text-[12px] text-neutral-500 italic">
                        No Breakdown entries yet.
                    </li>
                ) : rows.map((row) => (
                    <li
                        key={row.id}
                        data-testid={`dept-breakdown-entry-${row.id}`}
                        className="rounded-lg border border-white/5 bg-black/20 p-2"
                    >
                        {editingId === row.id ? (
                            <div className="space-y-1">
                                <Input
                                    data-testid={`dept-breakdown-edit-title-${row.id}`}
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    placeholder="Title (optional)"
                                    className="text-[12px] h-8"
                                />
                                <Textarea
                                    data-testid={`dept-breakdown-edit-body-${row.id}`}
                                    value={editBody}
                                    onChange={(e) => setEditBody(e.target.value)}
                                    rows={3}
                                    className="text-[12px]"
                                />
                                <div className="flex gap-1 justify-end">
                                    <Button
                                        variant="ghost" size="sm"
                                        onClick={() => setEditingId(null)}
                                        data-testid={`dept-breakdown-edit-cancel-${row.id}`}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={saveEdit}
                                        data-testid={`dept-breakdown-edit-save-${row.id}`}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        {row.title ? (
                                            <div className="text-[11px] text-white font-semibold">
                                                {row.title}
                                            </div>
                                        ) : null}
                                        <div className="text-[12px] text-neutral-200 whitespace-pre-wrap">
                                            {row.body}
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-1 items-center text-[9px] uppercase tracking-[0.15em]">
                                            {row.source === "ai_hm_breakdown" ? (
                                                <span
                                                    data-testid={`dept-breakdown-provenance-${row.id}`}
                                                    className="px-1.5 py-0.5 rounded-full border border-sky-400/40 bg-sky-500/10 text-sky-200"
                                                >
                                                    From Script{row.edited ? " · Edited" : ""}
                                                </span>
                                            ) : null}
                                            {row.needs_review ? (
                                                <span
                                                    data-testid={`dept-breakdown-needs-review-${row.id}`}
                                                    className="px-1.5 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200"
                                                >
                                                    Needs Review
                                                </span>
                                            ) : null}
                                        </div>
                                        {(row.unresolved_scene_refs || []).length
                                         || (row.unresolved_character_refs || []).length ? (
                                            <div className="mt-1 text-[10px] text-amber-200/80 space-y-0.5">
                                                {(row.unresolved_scene_refs || []).map((u, i) => (
                                                    <div key={`s-${i}`}>
                                                        ↳ Scene {u.episode ? `${u.episode}.` : ""}{u.scene_number} · {u.reason}
                                                    </div>
                                                ))}
                                                {(row.unresolved_character_refs || []).map((u, i) => (
                                                    <div key={`c-${i}`}>
                                                        ↳ Character {u.character_name} · {u.reason}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                    {canContribute ? (
                                        <div className="flex flex-col gap-1">
                                            <button
                                                type="button"
                                                data-testid={`dept-breakdown-edit-${row.id}`}
                                                onClick={() => beginEdit(row)}
                                                className="tap text-[10px] px-2 py-0.5 rounded-md border border-white/10 text-neutral-300 hover:bg-white/5"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`dept-breakdown-delete-${row.id}`}
                                                onClick={() => remove(row.id)}
                                                className="tap text-[10px] px-2 py-0.5 rounded-md border border-red-400/30 text-red-200 hover:bg-red-500/10"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            {canContribute ? (
                <div className="mt-3 space-y-1">
                    <Input
                        data-testid="dept-breakdown-new-title"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Title (optional)"
                        className="text-[12px] h-8"
                    />
                    <Textarea
                        data-testid="dept-breakdown-new-body"
                        value={newBody}
                        onChange={(e) => setNewBody(e.target.value)}
                        placeholder="Add a Breakdown note here…"
                        rows={2}
                        className="text-[12px]"
                    />
                    <div className="flex items-center justify-end">
                        <Button
                            size="sm"
                            data-testid="dept-breakdown-add"
                            onClick={createManual}
                            disabled={creating || !newBody.trim()}
                        >
                            {creating ? "Adding…" : "Add"}
                        </Button>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
